import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Dialog } from "@mui/material";
import Label from "../../../../components/form_fields/Label";
import OrangeButton from "../../../../components/buttons/OrangeButton";
import FormDescriptionInput from "../../../../components/form_fields/FormDescriptionInput";
import FormCodeInput from "../../../../components/form_fields/FormCodeInput";
import MoneyInput from "../../../../components/form_fields/MoneyInput";
import MultiSelectField from "../../../../components/form_fields/MultiSelectField";
import APISelectField from "../../../../components/form_fields/APISelectField";
import {
	useCreateCustomTaxRelief,
	useUpdateCustomTaxRelief,
} from "../../../../services/custom-tax-relief.service";
import { useManageTableWithSearchParamsData } from "../../../../helpers/useManageTable";
import useCompanyStore from "../../../../state-management/useCompanyStore";
import useEditFormStore from "../../../../state-management/useEditFormStore";
import { EmployeeDto } from "../../../../models/employee.dto";
import { EmployeeBandDto } from "../../../../models/employee-band.dto";
import { CustomTaxReliefGroupDto } from "../../../../models/custom-tax-relief-group.dto";
import { manageResourceIds } from "../../../../helpers/appHelpers";
import { PATHS } from "../../../../routes/routes.paths";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
	amount: number;
	active: boolean;
	groupId?: number;
	addEmployeeIds?: number[];
	removeEmployeeIds?: number[];
	addEmployeeBandIds?: number[];
	removeEmployeeBandIds?: number[];
}

interface ICustomTaxReliefModalForm {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	title: string;
	isEditForm: boolean;
	tableAPIRef: React.MutableRefObject<any>;
	reloadTableData: () => void;
	employees: EmployeeDto[];
	employeeBands: EmployeeBandDto[];
	customTaxReliefGroups: CustomTaxReliefGroupDto[];
}

const CustomTaxReliefModalForm = ({
	open,
	setOpen,
	title,
	isEditForm,
	tableAPIRef,
	reloadTableData,
	employees,
	employeeBands,
	customTaxReliefGroups,
}: ICustomTaxReliefModalForm) => {
	const { currentCompany } = useCompanyStore();
	const { formDataValues } = useEditFormStore();
	const [uploading, setUploading] = useState(false);
	const [employeeIds, setEmployeeIds] = useState<number[]>([]);
	const [employeeBandIds, setEmployeeBandIds] = useState<number[]>([]);

	const isGrouped = isEditForm && !!formDataValues?.groupId;

	const {
		register,
		setValue,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: isEditForm
			? {
					name: formDataValues?.name,
					code: formDataValues?.code,
					description: formDataValues?.description,
					amount: formDataValues?.amount,
					active: formDataValues?.active,
					groupId: formDataValues?.groupId ?? undefined,
				}
			: undefined,
	});

	useEffect(() => {
		if (open) {
			reset(
				isEditForm
					? {
							name: formDataValues?.name,
							code: formDataValues?.code,
							description: formDataValues?.description,
							amount: formDataValues?.amount,
							active: formDataValues?.active,
							groupId: formDataValues?.groupId ?? undefined,
						}
					: { name: "", code: "", description: "", active: true },
			);
			setEmployeeIds([]);
			setEmployeeBandIds([]);
		}
	}, [open, isEditForm, formDataValues, reset]);

	const { mutate: createRelief } = useCreateCustomTaxRelief();
	const { mutate: updateRelief } = useUpdateCustomTaxRelief();

	const onSubmit: SubmitHandler<IFormInputs> = (data) => {
		setUploading(true);

		if (isEditForm) {
			const { addIds: addEmployeeIds, removeIds: removeEmployeeIds } =
				manageResourceIds({
					newData: employeeIds,
					oldData:
						(formDataValues?.employeeCustomTaxReliefs?.map(
							(r: any) => r?.employee?.id,
						) as number[]) || [],
				});
			const { addIds: addEmployeeBandIds, removeIds: removeEmployeeBandIds } =
				manageResourceIds({
					newData: employeeBandIds,
					oldData:
						(formDataValues?.employeeBandCustomTaxReliefs?.map(
							(r: any) => r?.employeeBand?.id,
						) as number[]) || [],
				});

			updateRelief(
				{
					payload: {
						...data,
						addEmployeeIds,
						removeEmployeeIds,
						addEmployeeBandIds,
						removeEmployeeBandIds,
					},
					id: formDataValues?.id,
				},
				{
					onSuccess: () => {
						reloadTableData();
						setOpen(false);
					},
					onSettled: () => setUploading(false),
				},
			);
		} else {
			const optionalValues: any = {};
			if (employeeIds.length) optionalValues.employeeIds = employeeIds;
			if (employeeBandIds.length)
				optionalValues.employeeBandIds = employeeBandIds;

			createRelief(
				{
					payload: {
						...data,
						...optionalValues,
						companyId: currentCompany?.id as number,
					},
				},
				{
					onSuccess: () => {
						reloadTableData();
						setOpen(false);
					},
					onSettled: () => setUploading(false),
				},
			);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={() => setOpen(false)}
			PaperProps={{ className: "rounded-xl" }}
			maxWidth="md"
			fullWidth
		>
			<div className="px-8 pt-8 pb-6">
				<h2 className="font-semibold text-lg mb-6">{title}</h2>

				{isGrouped && (
					<p className="text-sm text-amber-600 bg-amber-50 rounded-lg px-4 py-2 mb-4">
						Some fields are managed by the custom tax relief group and cannot be
						edited here.
					</p>
				)}

				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-2 gap-x-4 gap-y-6">
						{/* Name */}
						<div className="w-full">
							<Label title="Name" for="name" />
							<input
								placeholder="Enter Name"
								type="text"
								disabled={isGrouped}
								{...register("name", { required: "Enter name here" })}
								id="name"
								className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
									isGrouped ? "opacity-50 cursor-not-allowed" : ""
								} ${
									errors.name
										? "border border-red-500 focus:border-red-500 focus:outline-red-500"
										: "border-none"
								}`}
							/>
							{errors.name && (
								<p className="text-red-500 text-sm mt-1">
									{errors.name.message}
								</p>
							)}
						</div>

						{/* Code */}
						<div className="w-full">
							<Label title="Unique Code" for="code" />
							<FormCodeInput
								register={register}
								attributes={{
									disabled: isGrouped,
									className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										isGrouped ? "opacity-50 cursor-not-allowed" : ""
									} ${
										errors.code
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`,
								}}
							/>
							{errors.code && (
								<p className="text-red-500 text-sm mt-1">
									{errors.code.message}
								</p>
							)}
						</div>

						{/* Description */}
						<div className="col-span-2 w-full">
							<Label title="Description" for="description" optional />
							<FormDescriptionInput
								required={false}
								attributes={{
									className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
										isGrouped ? "opacity-50 cursor-not-allowed" : ""
									} ${
										errors.description
											? "border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`,
								}}
								register={register}
								disabled={isGrouped}
							/>
							{errors.description && (
								<p className="text-red-500 text-sm mt-1">
									{errors.description.message}
								</p>
							)}
						</div>

						{/* Tax Relief Group */}
						{isEditForm && (
							<div className="col-span-2 w-full">
								<Label title="Custom Tax Relief Group" for="groupId" optional />
								<APISelectField
									attributes={{
										id: "groupId",
										disabled: isGrouped,
									}}
									placeholder="Select Custom Tax Relief Group"
									register={register}
									name="groupId"
									options={customTaxReliefGroups}
									pathname={PATHS.SUB_COMPANY_SETTINGS.CUSTOM_TAX_RELIEF_GROUPS}
									required={false}
									error={errors.groupId}
									valueAsNumber
								/>
								{errors.groupId && (
									<p className="text-red-500 text-sm mt-1">
										{errors.groupId.message}
									</p>
								)}
							</div>
						)}

						{/* Employees */}
						<div className="w-full flex flex-col gap-2">
							<Label title="Assign Employees" for="employeeIds" optional />
							<MultiSelectField
								key={"employeeIds"}
								options={employees}
								setResourceIds={setEmployeeIds}
								placeholder="Select Employees"
								pathname={PATHS.EMPLOYEES}
								required={false}
								mapTexts={["firstName", "lastName"]}
								defaultValues={
									isEditForm
										? ((formDataValues?.employeeCustomTaxReliefs?.map(
												(r: any) => r?.employee?.id,
											) as number[]) ?? [])
										: []
								}
								disabled={isGrouped}
							/>
						</div>

						{/* Employee Bands */}
						<div className="w-full flex flex-col gap-2">
							<Label
								title="Assign Employee Bands"
								for="employeeBandIds"
								optional
							/>
							<MultiSelectField
								key={"employeeBandIds"}
								options={employeeBands}
								setResourceIds={setEmployeeBandIds}
								placeholder="Select Bands"
								pathname={PATHS.SUB_COMPANY_SETTINGS.BANDS}
								required={false}
								defaultValues={
									isEditForm
										? ((formDataValues?.employeeBandCustomTaxReliefs?.map(
												(r: any) => r?.employeeBand?.id,
											) as number[]) ?? [])
										: []
								}
							/>
						</div>

						{/* Amount */}
						<div className="w-full">
							<Label title="Amount" for="amount" />
							<MoneyInput
								attributes={{ id: "amount", placeholder: "0" }}
								defaultValue={isEditForm ? formDataValues?.amount : undefined}
								register={register}
								name="amount"
								setValue={setValue}
								required="Enter amount here"
								error={errors.amount}
							/>
							{errors.amount && (
								<p className="text-red-500 text-sm mt-1">
									{errors.amount.message}
								</p>
							)}
						</div>

						{/* Active */}
						<div className="col-span-2 flex space-x-3 items-center">
							<input
								type="checkbox"
								{...register("active")}
								id="active"
								disabled={isGrouped}
								defaultChecked={isEditForm ? formDataValues?.active : true}
								className={isGrouped ? "opacity-50 cursor-not-allowed" : ""}
							/>
							<label htmlFor="active" className={isGrouped ? "opacity-50" : ""}>
								Active
							</label>
						</div>
					</div>

					<div className="w-full flex items-center justify-between mt-8">
						<button
							type="button"
							onClick={() => setOpen(false)}
							className="px-10 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
						>
							Cancel
						</button>
						<OrangeButton
							title={uploading ? "Saving..." : title}
							type="submit"
							className="h-14 px-8"
							disabled={uploading}
						/>
					</div>
				</form>
			</div>
		</Dialog>
	);
};

export default CustomTaxReliefModalForm;
